import type { ContentType } from "../../src/types/contents/contents";
import type { Exercise } from "../../src/types/contents/exercise";
import type { Tag } from "../../src/types/contents/tag";
import type { Tip } from "../../src/types/contents/tip";
import type { Workout } from "../../src/types/contents/workout";

import { auth } from "./auth";
import type { ApiProgram } from "./types";

const API_HOST = process.env.API_HOST;

const getBearerToken = async () => {
  const token = await auth.currentUser?.getIdToken();
  return `Bearer ${token}`;
};

export const getContentList = async <T>(
  contentType: ContentType,
): Promise<T[]> => {
  const response = await fetch(`${API_HOST}/api/${contentType}/all`, {
    headers: {
      Authorization: await getBearerToken(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${contentType}s`);
  }

  return response.json();
};

const getContentById = async <T>(
  contentType: ContentType,
  id: string,
): Promise<T> => {
  const response = await fetch(`${API_HOST}/api/${contentType}/${id}`, {
    headers: {
      Authorization: await getBearerToken(),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${contentType} with id ${id}`);
  }

  return response.json();
};

export const getExercise = async (id: string): Promise<Exercise<"api">> =>
  getContentById<Exercise<"api">>("exercise", id);

export const getTip = async (id: string): Promise<Tip<"api">> =>
  getContentById<Tip<"api">>("tip", id);

export const getWorkout = async (id: string): Promise<Workout<"api">> =>
  getContentById<Workout<"api">>("workout", id);

export const getProgram = async (id: string): Promise<ApiProgram> =>
  getContentById<ApiProgram>("program", id);

const createContent = async (
  contentType: ContentType,
  content: unknown,
): Promise<{ id?: number }> => {
  const response = await fetch(`${API_HOST}/api/${contentType}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: await getBearerToken(),
    },
    body: JSON.stringify(content),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to create ${contentType}, status: ${await response.text()}`,
    );
  }

  return response.json();
};

export const createExercise = async (
  exercise: Exercise<"api">,
): Promise<{ id?: number }> => createContent("exercise", exercise);

export const createTip = async (tip: Tip<"api">): Promise<{ id?: number }> =>
  createContent("tip", tip);

export const createWorkout = async (
  workout: Workout<"api">,
): Promise<{ id?: number }> => createContent("workout", workout);

export const createProgram = async (
  program: ApiProgram,
): Promise<{ id?: number }> => createContent("program", program);

const updateContent = async (
  contentType: ContentType,
  content: unknown,
): Promise<void> => {
  const response = await fetch(`${API_HOST}/api/${contentType}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: await getBearerToken(),
    },
    body: JSON.stringify(content),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to update ${contentType}, status: ${await response.text()}`,
    );
  }
};

export const updateExercise = async (
  exercise: Exercise<"api">,
): Promise<void> => updateContent("exercise", exercise);

export const updateTip = async (tip: Tip<"api">): Promise<void> =>
  updateContent("tip", tip);

export const updateWorkout = async (workout: Workout<"api">): Promise<void> =>
  updateContent("workout", workout);

export const updateProgram = async (program: ApiProgram): Promise<void> =>
  updateContent("program", program);

export const uploadMedia = async (file: File, publicIdOverride?: string) => {
  const formData = new FormData();
  formData.append("image", file);

  let url = `${API_HOST}/api/media`;
  if (publicIdOverride) {
    url = `${url}?publicIdOverride=${publicIdOverride}`;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: await getBearerToken(),
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to upload media");
  }

  return response.json();
};

export const getTags = async (): Promise<Tag[]> => {
  const response = await fetch(`${API_HOST}/api/tag/all`, {
    headers: {
      Authorization: await getBearerToken(),
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch tags");
  }

  return response.json();
};
